import { ChangeDetectionStrategy, Component, computed, input, inject } from '@angular/core';
import { Timeline } from 'primeng/timeline';
import { Application } from '../../model/application';
import { ApartmentDto } from 'homey-housing-lottery-app-dto';

const TIMELINE_TIME_LABELS = {
  followingWeeks: 'Following weeks',
  followingMonths: 'Following months',
  comingWeeks: 'Coming weeks',
  comingMonths: 'Coming months',
} as const;

const TIMELINE_STATUS_TEMPLATES: Record<string, string> = {
  applicationSubmitted: 'Application submitted',
  lotteryCloses: 'Lottery closes',
  lotteryClosed: 'Lottery closed',
  receiveLotteryNumber: 'Receive lottery nr.',
  selection: 'Selection',
  applied: 'Applied',
  lotteryNumber: 'Lottery nr.: {{number}}',
  lotteryNumberReceived: 'Lottery nr. received',
  notSelected: 'Not selected',
  unlikelyToBeSelected: 'Unlikely to be selected',
  selected: 'Selected',
};

const statusLabel = (key: keyof typeof TIMELINE_STATUS_TEMPLATES, params?: { number?: number }): string => {
  let template = TIMELINE_STATUS_TEMPLATES[key];
  if (key === 'lotteryNumber' && params?.number !== undefined) {
    return template.replace('{{number}}', String(params.number));
  }
  return template;
};

@Component({
  selector: 'homey-lottery-timeline',
  imports: [Timeline],
  templateUrl: './lottery-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LotteryTimelineComponent {
  public readonly lottery = input.required<ApartmentDto>();
  public readonly application = input.required<Application>();

  private formatShortDateOrUndefined(date: string | undefined): string | undefined {
    if (!date) return undefined;
    // Keep non-breaking space between month and day
    return new Date(date)
      .toLocaleDateString('default', { month: 'short', day: 'numeric' })
      .replace(' ', '\u00A0');
  }

  protected readonly isWithdrawn = computed(() => {
    const application = this.application();
    return application.status === 'not-selected' && application.reason === 'withdrawn';
  });

  protected readonly timelineItems = computed(() => {
    const lottery = this.lottery();
    const application = this.application();


    switch (application.status) {
      case 'not-synced':
        return [
          { status: statusLabel('applicationSubmitted'),                                                               icon: 'pi pi-envelope',  color: 'bg-blue-500' },
          { status: statusLabel('lotteryCloses'),                date: this.formatShortDateOrUndefined(lottery.endDate), icon: 'pi pi-clock',     color: 'bg-blue-400 animate-pulse' },
          { status: statusLabel('receiveLotteryNumber'),         date: TIMELINE_TIME_LABELS.followingWeeks,                                   icon: 'pi pi-receipt',   color: 'bg-gray-400' },
          { status: statusLabel('selection'),                    date: TIMELINE_TIME_LABELS.followingMonths,                                  icon: 'pi pi-file-check',color: 'bg-gray-400' }
        ];

      case 'applied':
        switch (lottery.status) {
          case 'open':
            return [
              { status: statusLabel('applicationSubmitted'),      icon: 'pi pi-envelope',  color: 'bg-blue-500' },
              { status: statusLabel('lotteryCloses'),             date: this.formatShortDateOrUndefined(lottery.endDate),       icon: 'pi pi-clock',     color: 'bg-blue-400 animate-pulse' },
              { status: statusLabel('receiveLotteryNumber'),      date: TIMELINE_TIME_LABELS.followingWeeks,                                         icon: 'pi pi-receipt',   color: 'bg-gray-400' },
              { status: statusLabel('selection'),                 date: TIMELINE_TIME_LABELS.followingMonths,                                        icon: 'pi pi-file-check',color: 'bg-gray-400' }
            ];
          case 'closed':
            return [
              { status: statusLabel('applied'),                   date: this.formatShortDateOrUndefined(application.appliedAt), icon: 'pi pi-envelope',  color: 'bg-blue-500' },
              { status: statusLabel('lotteryClosed'),             date: this.formatShortDateOrUndefined(lottery.endDate),       icon: 'pi pi-clock',     color: 'bg-blue-500' },
              { status: statusLabel('receiveLotteryNumber'),      date: TIMELINE_TIME_LABELS.comingWeeks,                                            icon: 'pi pi-receipt',   color: 'bg-blue-400 animate-pulse' },
              { status: statusLabel('selection'),                 date: TIMELINE_TIME_LABELS.followingMonths,                                        icon: 'pi pi-file-check',color: 'bg-gray-400' }
            ];
        }
        break;

      case 'pending-selection':
        return [
          { status: statusLabel('applied'),                      date: this.formatShortDateOrUndefined(application.appliedAt),           icon: 'pi pi-envelope', color: 'bg-blue-500' },
          { status: statusLabel('lotteryClosed'),                date: this.formatShortDateOrUndefined(lottery.endDate),                 icon: 'pi pi-clock',    color: 'bg-blue-500' },
          { status: statusLabel('lotteryNumber', { number: application.applicationNumber }), date: this.formatShortDateOrUndefined(application.receivedApplicationNumberAt), icon: 'pi pi-receipt', color: 'bg-blue-500' },
          { status: statusLabel('selection'),                    date: TIMELINE_TIME_LABELS.comingMonths,                                                     icon: 'pi pi-file-check', color: 'bg-blue-400 animate-pulse' }
        ];

      case 'not-selected': {
        const notSelectedLabel = application.reason === 'estimated'
          ? statusLabel('unlikelyToBeSelected')
          : statusLabel('notSelected');

        const lotteryNumberStep = application.applicationNumber !== undefined
          ? { status: statusLabel('lotteryNumber', { number: application.applicationNumber }), date: this.formatShortDateOrUndefined(application.receivedApplicationNumberAt), icon: 'pi pi-receipt', color: 'bg-blue-500' }
          : { status: '',                                          date: undefined,                                                                    icon: 'pi pi-receipt', color: 'bg-gray-400' };

        return [
          { status: statusLabel('applied'),                      date: this.formatShortDateOrUndefined(application.appliedAt),           icon: 'pi pi-envelope', color: 'bg-blue-500' },
          { status: statusLabel('lotteryClosed'),                date: this.formatShortDateOrUndefined(lottery.endDate),                 icon: 'pi pi-clock',    color: 'bg-blue-500' },
          lotteryNumberStep,
          { status: notSelectedLabel,                            date: this.formatShortDateOrUndefined(application.selectionAt),         icon: 'pi pi-times',    color: 'bg-red-500' }
        ];
      }

      case 'selected':
        return [
          { status: statusLabel('applied'),                      date: this.formatShortDateOrUndefined(application.appliedAt),           icon: 'pi pi-envelope', color: 'bg-blue-500' },
          { status: statusLabel('lotteryClosed'),                date: this.formatShortDateOrUndefined(lottery.endDate),                 icon: 'pi pi-clock',    color: 'bg-blue-500' },
          { status: statusLabel('lotteryNumberReceived'),        date: this.formatShortDateOrUndefined(application.receivedApplicationNumberAt), icon: 'pi pi-receipt', color: 'bg-blue-500' },
          { status: statusLabel('selected'),                     date: this.formatShortDateOrUndefined(application.selectionAt),         icon: 'pi pi-check',    color: 'bg-green-500' }
        ];
    }

    return []; // fallback
  });
}
